import React, { useState, useEffect } from 'react'
import {
  PlusIcon,
  CheckCircleIcon,
  PlayIcon,
  RefreshCwIcon,
  FileTextIcon,
  DollarSignIcon,
  CalendarDaysIcon,
  SendIcon,
  PenToolIcon,
  XIcon,
  TrashIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useNavigate } from 'react-router-dom'
import {
  MobilePage,
  MobilePageHeader,
  MobileListRow,
  MobileEmptyState,
  mobile,
} from '../components/mobile'
import { ContractScheduleModal } from '../components/ContractScheduleModal'
import { ColumnSelector } from '../components/ColumnSelector'
import { ListFilterSortBar } from '../components/ListFilterSortBar'
import { useColumnSelector } from '../hooks/useColumnSelector'
import { logActivity } from '../utils/activityLogger'
import {
  VisitGroupEditor,
  ContractVisitGroup,
  validateVisitGroups,
} from '../components/VisitGroupEditor'
import {
  getGroupAssignments,
  mappingRowFromAssignment,
  normalizeVisitGroupFromDb,
  repairVisitGroupsMissingServices,
} from '../lib/visitGroupBoardOps'
import { useVisitGroupTemplates } from '../hooks/useVisitGroupTemplates'
import { useCombinationRules } from '../hooks/useCombinationRules'
import {
  buildDsnyVisitBoardServices,
  ensureDsnyVisitGroupsForDisplay,
  hydrateDsnyScheduleRowsFromContractServices,
} from '../lib/dsnyContractPersistence'
import { generateJobsForContract } from '../api/jobGeneration'
import {
  getContractStatusColor,
  formatContractStatusLabel,
  contractWorkflowType,
} from '../lib/contractStatusWorkflow'
import { formatContractTypeLabel } from '../types/agreement'
import { useDivision } from '../contexts/DivisionContext'
import { recordMatchesDivision } from '../lib/operationalDivisions'
import {
  EMPTY_DATE_RANGE,
  matchesDateRange,
  matchesMultiSelect,
  sortItems,
  uniqueFilterOptions,
  type DateRange,
  type SortDir,
} from '../lib/listFilterSort'

interface Contract {
  id: string
  contract_number: string
  title: string
  description?: string
  contract_type: 'one_time' | 'recurring' | 'as_needed'
  service_type?: string
  operational_division_id?: string | null
  total_amount?: number
  billing_frequency?: string
  is_recurring: boolean
  recurrence_type?: string
  recurrence_days?: number[]
  dsny_integration?: boolean
  dsny_pickup_days?: string[]
  dsny_collection_types?: string[]
  interior_cleaning_schedule?: string[]
  status:
    | 'draft'
    | 'pending_signature'
    | 'signed'
    | 'in_process'
    | 'active'
    | 'paused'
    | 'completed'
    | 'cancelled'
    | 'expired'
  start_date?: string
  end_date?: string
  signed_date?: string
  payment_terms?: string
  notes?: string
  created_at: string
  updated_at: string
  customer?: {
    id: string
    company_name?: string
    contact_first_name?: string
    contact_last_name?: string
  }
  property?: {
    id: string
    name?: string
    address?: string
    city?: string
    state?: string
  }
  proposal?: {
    id: string
    title?: string
    proposal_number?: string
  }
}

type ContractSection = 'recurring' | 'one_time' | 'as_needed' | 'all'
type RecurringStatusTab = 'draft' | 'pending_signature' | 'active' | 'completed' | 'other'
type OneTimeStatusTab =
  | 'draft'
  | 'pending_signature'
  | 'signed'
  | 'in_process'
  | 'completed'
  | 'other'
type StatusTab = RecurringStatusTab | OneTimeStatusTab

const RECURRING_STATUS_TABS: { key: RecurringStatusTab; label: string }[] = [
  { key: 'draft', label: 'Draft' },
  { key: 'pending_signature', label: 'Pending Signature' },
  { key: 'active', label: 'Active' },
  { key: 'completed', label: 'Completed' },
  { key: 'other', label: 'Other' },
]

const ONE_TIME_STATUS_TABS: { key: OneTimeStatusTab; label: string }[] = [
  { key: 'draft', label: 'Draft' },
  { key: 'pending_signature', label: 'Pending Signature' },
  { key: 'signed', label: 'Signed' },
  { key: 'in_process', label: 'In Process' },
  { key: 'completed', label: 'Completed' },
  { key: 'other', label: 'Other' },
]

const RECURRING_OTHER = ['paused', 'cancelled', 'expired']
const ONE_TIME_OTHER = ['cancelled', 'expired', 'active', 'paused']

export function Contracts() {
  const navigate = useNavigate()
  const { currentDivision } = useDivision()
  const [contracts, setContracts] = useState<Contract[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [section, setSection] = useState<ContractSection>('recurring')
  const [statusTab, setStatusTab] = useState<StatusTab>('active')
  const [customerFilter, setCustomerFilter] = useState<string[]>([])
  const [propertyFilter, setPropertyFilter] = useState<string[]>([])
  const [startRange, setStartRange] = useState<DateRange>(EMPTY_DATE_RANGE)
  const [createdRange, setCreatedRange] = useState<DateRange>(EMPTY_DATE_RANGE)
  const [sortBy, setSortBy] = useState('number')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [scheduleModal, setScheduleModal] = useState<{
    isOpen: boolean
    contract: Contract | null
  }>({
    isOpen: false,
    contract: null,
  })
  type WorkflowAction = 'send' | 'sign' | 'start' | 'complete'
  const [workflowModal, setWorkflowModal] = useState<{
    isOpen: boolean
    contract: Contract | null
    action: WorkflowAction | null
  }>({
    isOpen: false,
    contract: null,
    action: null,
  })
  const [workflowSaving, setWorkflowSaving] = useState(false)
  const { templates: visitGroupTemplates, saveGroupAsTemplate } = useVisitGroupTemplates()
  const { suggestGroupsDayCentric } = useCombinationRules()

  // Visit-group configurator modal — surfaced when Mark Signed is attempted on a
  // recurring contract whose visit groups are missing or incomplete.
  const [vgConfigurator, setVgConfigurator] = useState<{
    isOpen: boolean
    contract: Contract | null
    visitGroups: ContractVisitGroup[]
    contractServices: any[]
    loading: boolean
    saving: boolean
  }>({
    isOpen: false,
    contract: null,
    visitGroups: [],
    contractServices: [],
    loading: false,
    saving: false,
  })

  useEffect(() => {
    fetchContracts()
  }, [currentDivision])

  const fetchContracts = async () => {
    try {
      setLoading(true)

      const { data: contractsData, error: contractsError } = await supabase
        .from('contracts')
        .select(
          `
          *,
          customer:customers(id, company_name, contact_first_name, contact_last_name),
          property:properties(id, name, address, city, state),
          proposal:proposals!contracts_proposal_id_fkey(id, title, proposal_number)
        `
        )
        .order('created_at', { ascending: false })

      if (contractsError) throw contractsError

      console.log('Contracts - Fetched contracts data:', contractsData)
      contractsData?.forEach((contract, index) => {
        console.log(`Contract ${index + 1}:`, {
          id: contract.id,
          title: contract.title,
          is_recurring: contract.is_recurring,
          recurrence_days: contract.recurrence_days,
          dsny_integration: contract.dsny_integration,
        })
      })

      setContracts(
        (contractsData || []).filter((contract) =>
          recordMatchesDivision(contract, currentDivision)
        )
      )
    } catch (error) {
      console.error('Error fetching contracts:', error)
    } finally {
      setLoading(false)
    }
  }

  const isAsNeededContract = (c: Contract) => c.contract_type === 'as_needed'
  const isRecurringContract = (c: Contract) =>
    !isAsNeededContract(c) && (c.contract_type === 'recurring' || !!c.is_recurring)

  const matchesSection = (contract: Contract): boolean => {
    if (section === 'all') return true
    if (section === 'recurring') return isRecurringContract(contract)
    if (section === 'as_needed') return isAsNeededContract(contract)
    return !isRecurringContract(contract) && !isAsNeededContract(contract)
  }

  const matchesStatusTab = (contract: Contract): boolean => {
    if (section === 'all') return true
    if (section === 'recurring' || section === 'as_needed') {
      switch (statusTab) {
        case 'draft':
          return contract.status === 'draft'
        case 'pending_signature':
          return contract.status === 'pending_signature'
        case 'active':
          return contract.status === 'active'
        case 'completed':
          return contract.status === 'completed'
        case 'other':
          return RECURRING_OTHER.includes(contract.status)
        default:
          return false
      }
    }
    // one_time
    switch (statusTab) {
      case 'draft':
        return contract.status === 'draft'
      case 'pending_signature':
        return contract.status === 'pending_signature'
      case 'signed':
        return contract.status === 'signed'
      case 'in_process':
        return contract.status === 'in_process'
      case 'completed':
        return contract.status === 'completed'
      case 'other':
        return ONE_TIME_OTHER.includes(contract.status)
      default:
        return false
    }
  }

  const contractCustomerName = (contract: Contract) =>
    contract.customer?.company_name ||
    `${contract.customer?.contact_first_name || ''} ${
      contract.customer?.contact_last_name || ''
    }`.trim()

  const contractPropertyName = (contract: Contract) =>
    contract.property?.name || contract.property?.address || ''

  const customerOptions = uniqueFilterOptions(contracts.map(contractCustomerName))
  const propertyOptions = uniqueFilterOptions(contracts.map(contractPropertyName))

  const filteredContracts = sortItems(
    contracts.filter((contract) => {
      const matchesSearch =
        contract.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contract.contract_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contract.customer?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contract.property?.address?.toLowerCase().includes(searchTerm.toLowerCase())

      return (
        matchesSearch &&
        matchesSection(contract) &&
        matchesStatusTab(contract) &&
        matchesMultiSelect(contractCustomerName(contract), customerFilter) &&
        matchesMultiSelect(contractPropertyName(contract), propertyFilter) &&
        matchesDateRange(contract.start_date, startRange) &&
        matchesDateRange(contract.created_at, createdRange)
      )
    }),
    (contract) => {
      switch (sortBy) {
        case 'title':
          return contract.title || ''
        case 'customer':
          return contract.customer?.company_name || ''
        case 'amount':
          return contract.total_amount || 0
        case 'status':
          return contract.status || ''
        case 'start':
          return contract.start_date || ''
        case 'created':
          return contract.created_at || ''
        default:
          return contract.contract_number || ''
      }
    },
    sortDir
  )

  const sectionContracts = contracts.filter(matchesSection)

  const statusTabCounts = (() => {
    const counts: Record<string, number> = {}
    const tabs =
      section === 'recurring' || section === 'as_needed'
        ? RECURRING_STATUS_TABS
        : section === 'one_time'
          ? ONE_TIME_STATUS_TABS
          : []
    for (const tab of tabs) {
      counts[tab.key] = sectionContracts.filter((c) => {
        if (section === 'recurring' || section === 'as_needed') {
          switch (tab.key) {
            case 'draft':
              return c.status === 'draft'
            case 'pending_signature':
              return c.status === 'pending_signature'
            case 'active':
              return c.status === 'active'
            case 'completed':
              return c.status === 'completed'
            case 'other':
              return RECURRING_OTHER.includes(c.status)
            default:
              return false
          }
        }
        switch (tab.key) {
          case 'draft':
            return c.status === 'draft'
          case 'pending_signature':
            return c.status === 'pending_signature'
          case 'signed':
            return c.status === 'signed'
          case 'in_process':
            return c.status === 'in_process'
          case 'completed':
            return c.status === 'completed'
          case 'other':
            return ONE_TIME_OTHER.includes(c.status)
          default:
            return false
        }
      }).length
    }
    return counts
  })()

  const sectionCounts = {
    recurring: contracts.filter((c) => isRecurringContract(c)).length,
    one_time: contracts.filter((c) => !isRecurringContract(c) && !isAsNeededContract(c)).length,
    as_needed: contracts.filter((c) => isAsNeededContract(c)).length,
    all: contracts.length,
  }

  const selectSection = (next: ContractSection) => {
    setSection(next)
    if (next === 'recurring' || next === 'as_needed') setStatusTab('active')
    else if (next === 'one_time') setStatusTab('signed')
  }

  const getStatusColor = getContractStatusColor

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'one_time':
        return 'bg-blue-100 text-blue-800'
      case 'recurring':
        return 'bg-purple-100 text-purple-800'
      case 'as_needed':
        return 'bg-teal-100 text-teal-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  const handleDeleteContract = async (contract: Contract) => {
    const label = contract.title || contract.contract_number || 'this contract'
    if (
      !window.confirm(
        `Are you sure you want to delete "${label}"? Linked jobs and billing records may block deletion. This action cannot be undone.`
      )
    ) {
      return
    }

    try {
      const { error } = await supabase.from('contracts').delete().eq('id', contract.id)
      if (error) throw error
      setContracts((prev) => prev.filter((c) => c.id !== contract.id))
    } catch (error: any) {
      console.error('Error deleting contract:', error)
      const msg = error?.message || ''
      if (msg.includes('foreign key') || msg.includes('referenced')) {
        alert(
          'Cannot delete this contract because it is linked to jobs, invoices, or other records. Remove those links first.'
        )
      } else {
        alert('Failed to delete contract: ' + (msg || 'Unknown error'))
      }
    }
  }

  const generateJobsFromContract = async (contractId: string) => {
    try {
      const results = await generateJobsForContract(contractId)
      alert(`Successfully generated ${results.length} job(s) from visit groups`)
      fetchContracts()
    } catch (error) {
      console.error('Error generating jobs for contract:', error)
      alert(
        'Error generating jobs: ' +
          (error instanceof Error ? error.message : 'Unknown error')
      )
    }
  }

  /** After status→active, seed the 90-day window. Never fail activation if regen throws. */
  const seedJobsAfterActivation = async (contract: Contract) => {
    if (!contract.is_recurring) return
    try {
      const start = new Date()
      start.setHours(0, 0, 0, 0)
      const end = new Date(start)
      end.setDate(end.getDate() + 90)
      if (contract.end_date) {
        const contractEnd = new Date(contract.end_date)
        if (contractEnd < end) end.setTime(contractEnd.getTime())
      }
      const results = await generateJobsForContract(contract.id, start, end)
      if (results.length > 0) {
        console.log(`Seeded ${results.length} job(s) after activating ${contract.contract_number}`)
      }
    } catch (err) {
      console.warn('Contract activated but job seeding failed:', err)
      alert(
        'Contract activated, but job generation failed. Use Generate Jobs on the contract or apply the latest job-generation migration, then retry.'
      )
    }
  }

  const runRecurringTopUpNow = async () => {
    try {
      const daysInput = window.prompt(
        'Run recurring top-up for next N days (same process as nightly). Enter N:',
        '30'
      )
      if (!daysInput) return
      const days = parseInt(daysInput, 10)
      if (Number.isNaN(days) || days <= 0) {
        alert('Please enter a valid positive number of days.')
        return
      }

      const startDate = new Date()
      const endDate = new Date(Date.now() + days * 24 * 60 * 60 * 1000)

      const { runRecurringJobsWindow } = await import('../api/jobGeneration')
      const preview = await runRecurringJobsWindow(startDate, endDate, true)
      const previewTotal = preview.reduce((sum, row) => sum + (row.jobs_created || 0), 0)
      const previewSkipped = preview.reduce((sum, row) => sum + (row.jobs_skipped || 0), 0)
      const previewErrors = preview.filter((row) => row.error_message).length

      const shouldRun = window.confirm(
        `Preview: ${previewTotal} new job(s), ${previewSkipped} already existed (skipped) across ${preview.length} recurring contract(s).` +
          (previewErrors > 0 ? `\n${previewErrors} contract(s) reported errors.` : '') +
          '\n\nRun now?'
      )
      if (!shouldRun) return

      const result = await runRecurringJobsWindow(startDate, endDate, false)
      const totalCreated = result.reduce((sum, row) => sum + (row.jobs_created || 0), 0)
      const totalSkipped = result.reduce((sum, row) => sum + (row.jobs_skipped || 0), 0)
      const totalErrors = result.filter((row) => row.error_message).length

      alert(
        `Recurring top-up completed.\nCreated: ${totalCreated} job(s)\nSkipped (already existed): ${totalSkipped}\nContracts processed: ${result.length}\nErrors: ${totalErrors}`
      )
      fetchContracts()
    } catch (error) {
      console.error('Error running recurring top-up:', error)
      alert('Error running recurring top-up: ' + (error as any).message)
    }
  }

  const openWorkflowModal = (contract: Contract, action: WorkflowAction) => {
    setWorkflowModal({ isOpen: true, contract, action })
  }

  const closeWorkflowModal = () => {
    if (workflowSaving) return
    setWorkflowModal({ isOpen: false, contract: null, action: null })
  }

  // Quickly check whether a recurring contract is ready to activate
  // (i.e., has at least one active visit group with days + service mappings).
  const checkContractVisitGroupsReady = async (
    contractId: string
  ): Promise<{ ready: boolean; errors: string[] }> => {
    try {
      const { data: groups, error } = await supabase
        .from('contract_visit_groups')
        .select(
          'id, name, recurrence_type, recurrence_days, is_active, contract_visit_group_services(contract_service_id)'
        )
        .eq('contract_id', contractId)
      if (error) throw error

      const normalized: ContractVisitGroup[] = (groups || []).map((g: any) => ({
        id: g.id,
        contract_id: contractId,
        name: g.name,
        recurrence_type: g.recurrence_type,
        recurrence_days: g.recurrence_days || [],
        is_active: g.is_active !== false,
        service_ids: (g.contract_visit_group_services || []).map(
          (m: any) => m.contract_service_id
        ),
      }))
      const errors = validateVisitGroups(normalized)
      if (errors.length > 0 && (groups || []).length > 0) {
        const { data: services } = await supabase
          .from('contract_services')
          .select('id, service_name, recurrence_days, dsny_integration')
          .eq('contract_id', contractId)
        const repaired = repairVisitGroupsMissingServices(normalized, services || [])
        const repairErrors = validateVisitGroups(repaired)
        if (repairErrors.length === 0) {
          for (const group of repaired) {
            if (group.id.startsWith('temp-')) continue
            const assignments = getGroupAssignments(group)
            if (assignments.length === 0) continue
            await supabase
              .from('contract_visit_group_services')
              .delete()
              .eq('visit_group_id', group.id)
            const rows = assignments.map((a) =>
              mappingRowFromAssignment(group.id, a.service_id, a)
            )
            const { error: mapErr } = await supabase
              .from('contract_visit_group_services')
              .insert(rows)
            if (mapErr) throw mapErr
          }
          return { ready: true, errors: [] }
        }
      }
      return { ready: errors.length === 0, errors }
    } catch (e) {
      console.error('checkContractVisitGroupsReady failed:', e)
      return { ready: false, errors: ['Failed to verify visit groups.'] }
    }
  }

  // Open the inline configurator for a contract that failed the gate.
  // Build a "Building Upkeep" service from a contract's saved DSNY schedule columns and
  // insert it into contract_services. Returns the inserted row(s), or [] when the contract
  // has no schedule to derive from (or the insert fails). Used to self-heal contracts that
  // have a schedule but no service rows.
  const deriveAndPersistDsnyService = async (contractId: string): Promise<any[]> => {
    try {
      const { data: sched } = await supabase
        .from('contracts')
        .select(
          'master_weekly_schedule, garbage_schedule, recycling_schedule, organics_schedule, bulk_schedule, interior_cleaning_schedule, dsny_pickup_days'
        )
        .eq('id', contractId)
        .single()
      if (!sched) return []

      const days: string[] =
        sched.master_weekly_schedule && sched.master_weekly_schedule.length > 0
          ? sched.master_weekly_schedule
          : Array.from(
              new Set([
                ...(sched.garbage_schedule || []),
                ...(sched.recycling_schedule || []),
                ...(sched.organics_schedule || []),
                ...(sched.bulk_schedule || []),
                ...(sched.interior_cleaning_schedule || []),
                ...(sched.dsny_pickup_days || []),
              ])
            )
      if (days.length === 0) return []

      const { data: inserted, error: insErr } = await supabase
        .from('contract_services')
        .insert({
          contract_id: contractId,
          service_type_id: null,
          service_name: 'Building Upkeep',
          amount: 0,
          recurrence_type: 'weekly',
          recurrence_days: days,
          dsny_integration: true,
          garbage_schedule: sched.garbage_schedule || [],
          recycling_schedule: sched.recycling_schedule || [],
          organics_schedule: sched.organics_schedule || [],
          bulk_schedule: sched.bulk_schedule || [],
          interior_cleaning_schedule: sched.interior_cleaning_schedule || [],
        })
        .select('id, service_name, recurrence_days, service_type_id')
      if (insErr) {
        console.error('Failed to derive DSNY service from schedule:', insErr)
        return []
      }
      return inserted || []
    } catch (e) {
      console.error('deriveAndPersistDsnyService error:', e)
      return []
    }
  }

  const openVisitGroupConfigurator = async (contract: Contract) => {
    setVgConfigurator({
      isOpen: true,
      contract,
      visitGroups: [],
      contractServices: [],
      loading: true,
      saving: false,
    })
    try {
      const [{ data: services }, { data: groups }] = await Promise.all([
        supabase
          .from('contract_services')
          .select(
            'id, service_name, service_item_id, service_type_id, amount, recurrence_type, recurrence_days, dsny_integration, garbage_schedule, recycling_schedule, organics_schedule, bulk_schedule, interior_cleaning_schedule'
          )
          .eq('contract_id', contract.id),
        supabase
          .from('contract_visit_groups')
          .select(
            'id, name, recurrence_type, recurrence_days, recurrence_weekday, recurrence_occurrence, recurrence_day_of_month, start_date, end_date, scheduled_start_time, scheduled_end_time, billing_driver, dsny_integration, dsny_pickup_days, is_active, assigned_crew_id, assigned_employee_id, contract_visit_group_services(contract_service_id, schedule_mode, recurrence_type, recurrence_days, recurrence_weekday, recurrence_occurrence, recurrence_day_of_month, recurrence_nearest_visit)'
          )
          .eq('contract_id', contract.id),
      ])

      let effectiveServices: any[] = services || []

      // Self-heal: a DSNY / recurring contract can have its schedule stored on the
      // contracts row (garbage/recycling/master weekly, etc.) but no contract_services
      // rows — e.g. when it was created from a proposal. Without any service the visit
      // groups can't be configured and the contract can't be activated. Derive a single
      // "Building Upkeep" service from the saved schedule and persist it so the contract
      // is permanently fixed and downstream views (details, jobs) populate too.
      if (effectiveServices.length === 0) {
        const derived = await deriveAndPersistDsnyService(contract.id)
        if (derived.length > 0) effectiveServices = derived
      }

      const isDsnyContract =
        !!contract.dsny_integration ||
        effectiveServices.some(
          (s) =>
            !!s.dsny_integration ||
            (Number(s.amount) > 0 &&
              String(s.service_name || '')
                .toLowerCase()
                .includes('upkeep')) ||
            String(s.service_name || '')
              .toLowerCase()
              .includes('exterior maintenance')
        )

      let normalizedGroups: ContractVisitGroup[] = (groups || []).map((g: any) =>
        normalizeVisitGroupFromDb(g, g.contract_visit_group_services || [], contract.id)
      )

      let editorServices: any[] = effectiveServices

      if (isDsnyContract && effectiveServices.length > 0) {
        normalizedGroups = ensureDsnyVisitGroupsForDisplay(
          normalizedGroups,
          effectiveServices,
          {
            contractId: contract.id,
            contractStartDate: contract.start_date,
            contractEndDate: contract.end_date,
          }
        )
        const mappings = normalizedGroups.flatMap((g) =>
          getGroupAssignments(g).map((a) => ({
            contract_service_id: a.service_id,
            schedule_mode: a.schedule_mode,
            recurrence_type: a.recurrence_type,
            recurrence_weekday: a.recurrence_weekday,
            recurrence_occurrence: a.recurrence_occurrence,
            recurrence_day_of_month: a.recurrence_day_of_month,
            recurrence_nearest_visit: a.recurrence_nearest_visit,
            visit_group_name: g.name,
            visit_group_dsny_integration: !!g.dsny_integration,
          }))
        )
        editorServices = buildDsnyVisitBoardServices(
          hydrateDsnyScheduleRowsFromContractServices(effectiveServices, mappings),
          effectiveServices
        )
      } else {
        const beforeRepairErrors = validateVisitGroups(normalizedGroups)
        if (beforeRepairErrors.length > 0 && effectiveServices.length > 0) {
          normalizedGroups = repairVisitGroupsMissingServices(
            normalizedGroups,
            effectiveServices
          )
        }
        const afterRepairErrors = validateVisitGroups(normalizedGroups)

        // If still invalid or no groups, auto-suggest using combination rules.
        if (afterRepairErrors.length > 0 && effectiveServices.length > 0) {
          normalizedGroups = suggestGroupsDayCentric(
            effectiveServices as any,
            contract.id,
            contract.start_date,
            contract.end_date
          )
        } else if (normalizedGroups.length === 0 && effectiveServices.length > 0) {
          normalizedGroups = suggestGroupsDayCentric(
            effectiveServices as any,
            contract.id,
            contract.start_date,
            contract.end_date
          )
        }
      }

      setVgConfigurator((prev) => ({
        ...prev,
        visitGroups: normalizedGroups,
        contractServices: editorServices,
        loading: false,
      }))
    } catch (e) {
      console.error('Failed to load visit-group configurator:', e)
      alert('Failed to load contract data: ' + (e as any)?.message)
      setVgConfigurator({
        isOpen: false,
        contract: null,
        visitGroups: [],
        contractServices: [],
        loading: false,
        saving: false,
      })
    }
  }

  const closeVisitGroupConfigurator = () => {
    if (vgConfigurator.saving) return
    setVgConfigurator({
      isOpen: false,
      contract: null,
      visitGroups: [],
      contractServices: [],
      loading: false,
      saving: false,
    })
  }

  // Persist the configurator's groups, then activate the contract.
  const saveConfiguratorAndActivate = async () => {
    const contract = vgConfigurator.contract
    if (!contract) return

    const errors = validateVisitGroups(vgConfigurator.visitGroups)
    if (errors.length > 0) {
      alert(
        ['Please fix the following before activating:', ...errors.map((e) => `• ${e}`)].join('\n')
      )
      return
    }

    try {
      setVgConfigurator((prev) => ({ ...prev, saving: true }))

      // Persist each visit group: insert if temp-, update otherwise. Also rewrite mappings.
      for (const group of vgConfigurator.visitGroups) {
        let persistedId = group.id
        if (group.id.startsWith('temp-')) {
          const { data: inserted, error: insertErr } = await supabase
            .from('contract_visit_groups')
            .insert({
              contract_id: contract.id,
              name: group.name,
              recurrence_type: group.recurrence_type,
              recurrence_days: group.recurrence_days,
              start_date: group.start_date || null,
              end_date: group.end_date || null,
              scheduled_start_time: group.scheduled_start_time || null,
              scheduled_end_time: group.scheduled_end_time || null,
              billing_driver: group.billing_driver || null,
              dsny_integration: group.dsny_integration || false,
              dsny_pickup_days: group.dsny_pickup_days || [],
              is_active: group.is_active !== false,
            })
            .select('id')
            .single()
          if (insertErr) throw insertErr
          persistedId = inserted.id
        } else {
          const { error: updateErr } = await supabase
            .from('contract_visit_groups')
            .update({
              name: group.name,
              recurrence_type: group.recurrence_type,
              recurrence_days: group.recurrence_days,
              start_date: group.start_date || null,
              end_date: group.end_date || null,
              scheduled_start_time: group.scheduled_start_time || null,
              scheduled_end_time: group.scheduled_end_time || null,
              billing_driver: group.billing_driver || null,
              dsny_integration: group.dsny_integration || false,
              dsny_pickup_days: group.dsny_pickup_days || [],
              is_active: group.is_active !== false,
            })
            .eq('id', group.id)
          if (updateErr) throw updateErr
        }

        await supabase
          .from('contract_visit_group_services')
          .delete()
          .eq('visit_group_id', persistedId)

        if (getGroupAssignments(group).length > 0) {
          const rows = getGroupAssignments(group).map((a) =>
            mappingRowFromAssignment(persistedId, a.service_id, a)
          )
          const { error: mapErr } = await supabase
            .from('contract_visit_group_services')
            .insert(rows)
          if (mapErr) throw mapErr
        }
      }

      // Activate the contract
      const today = new Date().toISOString().split('T')[0]
      const { error: activateErr } = await supabase
        .from('contracts')
        .update({ status: 'active', signed_date: today })
        .eq('id', contract.id)
      if (activateErr) throw activateErr

      await logActivity({
        activity_type: 'contract_signed',
        entity_type: 'contract',
        entity_id: contract.id,
        description: `Contract "${contract.title}" activated after visit-group configuration`,
        metadata: {
          previous_status: contract.status,
          new_status: 'active',
          signed_date: today,
          visit_groups_configured: vgConfigurator.visitGroups.length,
        },
      })

      await seedJobsAfterActivation(contract)

      await fetchContracts()
      closeVisitGroupConfigurator()
      setWorkflowModal({ isOpen: false, contract: null, action: null })
    } catch (e: any) {
      console.error('Failed to save & activate from configurator:', e)
      alert('Failed to save visit groups: ' + (e?.message || 'Unknown error'))
    } finally {
      setVgConfigurator((prev) => ({ ...prev, saving: false }))
    }
  }

  const confirmWorkflowAction = async () => {
    const { contract, action } = workflowModal
    if (!contract || !action) return

    try {
      setWorkflowSaving(true)

      if (action === 'send') {
        const { error } = await supabase
          .from('contracts')
          .update({ status: 'pending_signature' })
          .eq('id', contract.id)
        if (error) throw error

        await logActivity({
          activity_type: 'contract_sent_for_signature',
          entity_type: 'contract',
          entity_id: contract.id,
          description: `Contract "${contract.title}" sent for signature`,
          metadata: { previous_status: contract.status, new_status: 'pending_signature' },
        })
      } else if (action === 'sign') {
        // GATE: scheduled recurring contracts must have valid visit groups before activation.
        // Ongoing (as_needed) activates without visit groups.
        if (contract.is_recurring && contract.contract_type !== 'as_needed') {
          const { ready } = await checkContractVisitGroupsReady(contract.id)
          if (!ready) {
            setWorkflowSaving(false)
            await openVisitGroupConfigurator(contract)
            return
          }
        }

        // One-time -> signed; recurring & as_needed -> active
        const newStatus =
          contractWorkflowType(contract) === 'recurring' ? 'active' : 'signed'
        const today = new Date().toISOString().split('T')[0]
        const { error } = await supabase
          .from('contracts')
          .update({ status: newStatus, signed_date: today })
          .eq('id', contract.id)
        if (error) throw error

        await logActivity({
          activity_type: 'contract_signed',
          entity_type: 'contract',
          entity_id: contract.id,
          description:
            contractWorkflowType(contract) === 'recurring'
              ? `Contract "${contract.title}" marked as signed and activated`
              : `Contract "${contract.title}" marked as signed`,
          metadata: { previous_status: contract.status, new_status: newStatus, signed_date: today },
        })

        await seedJobsAfterActivation(contract)
      } else if (action === 'start') {
        const { error } = await supabase
          .from('contracts')
          .update({ status: 'in_process' })
          .eq('id', contract.id)
        if (error) throw error

        await logActivity({
          activity_type: 'contract_started',
          entity_type: 'contract',
          entity_id: contract.id,
          description: `Contract "${contract.title}" work started`,
          metadata: { previous_status: contract.status, new_status: 'in_process' },
        })
      } else if (action === 'complete') {
        const { error } = await supabase
          .from('contracts')
          .update({ status: 'completed' })
          .eq('id', contract.id)
        if (error) throw error

        await logActivity({
          activity_type: 'contract_completed',
          entity_type: 'contract',
          entity_id: contract.id,
          description: `Contract "${contract.title}" marked as completed`,
          metadata: { previous_status: contract.status, new_status: 'completed' },
        })
      }

      await fetchContracts()
      setWorkflowModal({ isOpen: false, contract: null, action: null })
    } catch (err: any) {
      console.error('Error updating contract status:', err)
      alert('Error updating contract: ' + (err?.message || 'Unknown error'))
    } finally {
      setWorkflowSaving(false)
    }
  }

  const viewContract = (contractId: string) => {
    console.log('Viewing contract:', contractId)
    navigate(`/contracts/details/${contractId}`)
  }

  const editContract = (contractId: string) => {
    console.log('Editing contract:', contractId)
    // Navigate to contract edit page
    navigate(`/contracts/edit/${contractId}`)
  }

  const viewSchedule = (contract: Contract) => {
    console.log('Viewing schedule for contract:', contract.id)
    // Open schedule modal
    setScheduleModal({
      isOpen: true,
      contract: contract,
    })
  }

  const closeScheduleModal = () => {
    setScheduleModal({
      isOpen: false,
      contract: null,
    })
  }

  const formatSchedule = (contract: Contract) => {
    if (contract.contract_type === 'as_needed') {
      return <span className="text-gray-500">As needed</span>
    }
    if (!contract.is_recurring) {
      return <span className="text-gray-500">One-time</span>
    }

    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    const recurringDays = contract.recurrence_days || []
    const daysText = recurringDays.map((day) => dayNames[day]).join(', ')

    let scheduleText = `${contract.recurrence_type || 'Custom'}`
    if (daysText) {
      scheduleText += ` (${daysText})`
    }

    // Add DSNY info if available
    if (contract.dsny_integration && contract.dsny_collection_types?.length) {
      const dsnyTypes = contract.dsny_collection_types.join(', ')
      scheduleText += ` + DSNY (${dsnyTypes})`
    }

    return (
      <div className="text-sm">
        <div className="text-gray-900">{scheduleText}</div>
        {contract.dsny_integration && <div className="text-xs text-blue-600">DSNY Integrated</div>}
      </div>
    )
  }

  const CONTRACT_COLUMNS = [
    { id: 'contract', label: 'Contract' },
    { id: 'customer', label: 'Customer' },
    { id: 'property', label: 'Property' },
    { id: 'type', label: 'Type' },
    { id: 'amount', label: 'Amount' },
    { id: 'status', label: 'Status' },
    { id: 'dates', label: 'Dates' },
    { id: 'actions', label: 'Actions' },
  ]
  const { visibleColumns, toggleColumn } = useColumnSelector(CONTRACT_COLUMNS, { storageKey: 'contracts' })

  const stats = {
    total: contracts.length,
    active: contracts.filter((c) => isRecurringContract(c) && c.status === 'active').length,
    recurring: contracts.filter((c) => isRecurringContract(c)).length,
    totalValue: contracts.reduce((sum, c) => sum + (c.total_amount || 0), 0),
  }

  return (
    <MobilePage>
      <MobilePageHeader
        title="Contracts"
        subtitle={
          <span className="hidden sm:inline">
            Manage service contracts and recurring agreements
          </span>
        }
        actions={
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={runRecurringTopUpNow}
              className={`hidden sm:inline-flex ${mobile.successBtn}`}
              title="Run the same recurring top-up process that nightly automation runs"
            >
              <RefreshCwIcon className="w-4 h-4" />
              Run Recurring Top-up Now
            </button>
            <button
              type="button"
              onClick={() => navigate('/contracts/create')}
              className={mobile.primaryBtn}
            >
              <PlusIcon className="w-4 h-4" />
              <span className="sm:hidden">New</span>
              <span className="hidden sm:inline">Create Contract</span>
            </button>
          </div>
        }
      />

      {/* Stats Cards */}
      <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileTextIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Contracts</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Recurring Active</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-violet-100 rounded-lg">
              <CalendarDaysIcon className="w-6 h-6 text-violet-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Recurring</p>
              <p className="text-2xl font-bold text-gray-900">{stats.recurring}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSignIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Value</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalValue)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Section + status tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex flex-nowrap overflow-x-auto border-b border-gray-200 scrollbar-none">
          {(
            [
              { key: 'recurring' as ContractSection, label: 'Recurring' },
              { key: 'as_needed' as ContractSection, label: 'Ongoing' },
              { key: 'one_time' as ContractSection, label: 'One-Time' },
              { key: 'all' as ContractSection, label: 'All' },
            ]
          ).map((s) => {
            const isActive = section === s.key
            return (
              <button
                key={s.key}
                onClick={() => selectSection(s.key)}
                className={`flex items-center gap-2 px-4 sm:px-5 min-h-[44px] text-sm font-semibold border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                  isActive
                    ? 'border-blue-500 text-blue-600 bg-blue-50/40'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {s.label}
                <span
                  className={`inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.5 text-xs font-semibold rounded-full ${
                    isActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {sectionCounts[s.key]}
                </span>
              </button>
            )
          })}
        </div>

        {section !== 'all' && (
          <div className="flex flex-nowrap overflow-x-auto border-b border-gray-200 bg-gray-50/40 scrollbar-none">
            {(section === 'recurring' || section === 'as_needed'
              ? RECURRING_STATUS_TABS
              : ONE_TIME_STATUS_TABS
            ).map(
              (tab) => {
                const isActive = statusTab === tab.key
                return (
                  <button
                    key={tab.key}
                    onClick={() => setStatusTab(tab.key)}
                    className={`flex items-center gap-2 px-3 sm:px-4 min-h-[44px] text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                      isActive
                        ? 'border-blue-500 text-blue-600 bg-white'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {tab.label}
                    <span
                      className={`inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.5 text-xs font-semibold rounded-full ${
                        isActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {statusTabCounts[tab.key] ?? 0}
                    </span>
                  </button>
                )
              }
            )}
          </div>
        )}

        {/* Filters row */}
        <ListFilterSortBar
          search={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search contracts..."
          className="rounded-none border-0 border-b border-gray-100 shadow-none"
          filters={[
            {
              type: 'multi',
              label: 'Customer',
              value: customerFilter,
              onChange: setCustomerFilter,
              options: customerOptions,
            },
            {
              type: 'multi',
              label: 'Property',
              value: propertyFilter,
              onChange: setPropertyFilter,
              options: propertyOptions,
            },
            {
              type: 'daterange',
              label: 'Start date',
              value: startRange,
              onChange: setStartRange,
            },
            {
              type: 'daterange',
              label: 'Created',
              value: createdRange,
              onChange: setCreatedRange,
            },
          ]}
          sortValue={sortBy}
          onSortChange={setSortBy}
          sortOptions={[
            { value: 'number', label: 'Number' },
            { value: 'title', label: 'Title' },
            { value: 'customer', label: 'Customer' },
            { value: 'amount', label: 'Amount' },
            { value: 'status', label: 'Status' },
            { value: 'start', label: 'Start date' },
            { value: 'created', label: 'Created' },
          ]}
          sortDir={sortDir}
          onSortDirChange={setSortDir}
          trailing={
            <div className="hidden lg:block">
              <ColumnSelector
                columns={CONTRACT_COLUMNS}
                visibleColumns={visibleColumns}
                onToggle={toggleColumn}
              />
            </div>
          }
        />
      </div>

      {/* Mobile card list */}
      <div className="native-mobile-list space-y-2">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredContracts.length === 0 ? (
          <MobileEmptyState
            icon={<FileTextIcon className="w-12 h-12" />}
            title="No contracts found"
            description={
              searchTerm || section !== 'all'
                ? 'Try adjusting your search, section, or status tab.'
                : 'Get started by creating a new contract.'
            }
          />
        ) : (
          filteredContracts.map((contract) => (
            <MobileListRow
              key={contract.id}
              onClick={() => viewContract(contract.id)}
              title={contract.title}
              subtitle={`#${contract.contract_number}`}
              badges={
                <span className={`${mobile.badge} ${getStatusColor(contract.status)}`}>
                  {formatContractStatusLabel(contract.status)}
                </span>
              }
              meta={
                <>
                  <div className="truncate">
                    {contract.customer?.company_name ||
                      `${contract.customer?.contact_first_name || ''} ${contract.customer?.contact_last_name || ''}`.trim() ||
                      '—'}
                  </div>
                  {contract.total_amount != null && (
                    <div className="font-semibold text-gray-900">
                      {formatCurrency(contract.total_amount)}
                    </div>
                  )}
                </>
              }
            />
          ))
        )}
      </div>

      {/* Contracts Table */}
      <div className="native-desktop-table hidden lg:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredContracts.length === 0 ? (
          <div className="text-center py-12">
            <FileTextIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No contracts found</h3>
            <p className="text-gray-500 mb-6">
              {searchTerm || section !== 'all'
                ? 'Try adjusting your search, section, or status tab.'
                : 'Get started by creating a new contract.'}
            </p>
          </div>
        ) : (
          <div className="w-full">
            <table className="w-full table-fixed divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {visibleColumns.has('contract') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contract
                    </th>
                  )}
                  {visibleColumns.has('customer') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                  )}
                  {visibleColumns.has('property') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Property
                    </th>
                  )}
                  {visibleColumns.has('type') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                  )}
                  {visibleColumns.has('amount') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                  )}
                  {visibleColumns.has('status') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  )}
                  {visibleColumns.has('dates') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dates
                    </th>
                  )}
                  {visibleColumns.has('actions') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredContracts.map((contract) => (
                  <tr key={contract.id} className="hover:bg-gray-50">
                    {visibleColumns.has('contract') && (
                      <td className="px-3 py-3 min-w-0">
                        <div className="min-w-0">
                          <button
                            onClick={() => viewContract(contract.id)}
                            className="text-sm font-medium text-gray-900 hover:text-blue-600 hover:underline text-left truncate block max-w-full"
                          >
                            {contract.title}
                          </button>
                          <div className="text-sm text-gray-500 truncate">#{contract.contract_number}</div>
                          {contract.proposal && (
                            <div className="text-xs text-gray-400 truncate">
                              From: {contract.proposal.proposal_number}
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                    {visibleColumns.has('customer') && (
                      <td className="px-3 py-3 min-w-0">
                        <div className="text-sm text-gray-900 truncate">
                          {contract.customer?.company_name ||
                            `${contract.customer?.contact_first_name} ${contract.customer?.contact_last_name}`}
                        </div>
                      </td>
                    )}
                    {visibleColumns.has('property') && (
                      <td className="px-3 py-3 min-w-0">
                        <div className="min-w-0">
                          <div className="text-sm text-gray-900 truncate">{contract.property?.name}</div>
                          <div className="text-sm text-gray-500 truncate">{contract.property?.address}</div>
                        </div>
                      </td>
                    )}
                    {visibleColumns.has('type') && (
                      <td className="px-3 py-3 min-w-0">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(
                            contract.contract_type
                          )}`}
                        >
                          {formatContractTypeLabel(contract.contract_type)}
                        </span>
                      </td>
                    )}
                    {visibleColumns.has('amount') && (
                      <td className="px-3 py-3 min-w-0 text-sm text-gray-900">
                        {contract.total_amount ? formatCurrency(contract.total_amount) : 'N/A'}
                      </td>
                    )}
                    {visibleColumns.has('status') && (
                      <td className="px-3 py-3 min-w-0">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                            contract.status
                          )}`}
                        >
                          {formatContractStatusLabel(contract.status)}
                        </span>
                      </td>
                    )}
                    {visibleColumns.has('dates') && (
                      <td className="px-3 py-3 min-w-0">
                        <div>
                          <div className="text-sm text-gray-900">
                            Start: {formatDate(contract.start_date || '')}
                          </div>
                          <div className="text-sm text-gray-500">
                            End: {formatDate(contract.end_date || '')}
                          </div>
                        </div>
                      </td>
                    )}
                    {visibleColumns.has('actions') && (
                      <td className="px-3 py-3 min-w-0">
                        <div className="flex flex-wrap items-center gap-1">
                          {contract.status === 'draft' && (
                            <button
                              onClick={() => openWorkflowModal(contract, 'send')}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                              title="Send for Signature"
                            >
                              <SendIcon className="w-3.5 h-3.5" />
                              Send for Signature
                            </button>
                          )}
                          {contract.status === 'pending_signature' && (
                            <button
                              onClick={() => openWorkflowModal(contract, 'sign')}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                              title="Mark as Signed"
                            >
                              <PenToolIcon className="w-3.5 h-3.5" />
                              Mark Signed
                            </button>
                          )}
                          {contractWorkflowType(contract) === 'one_time' &&
                            contract.status === 'signed' && (
                            <button
                              onClick={() => openWorkflowModal(contract, 'start')}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors"
                              title="Start Work"
                            >
                              <PlayIcon className="w-3.5 h-3.5" />
                              Start Work
                            </button>
                          )}
                          {contractWorkflowType(contract) === 'one_time' &&
                            contract.status === 'in_process' && (
                            <button
                              onClick={() => openWorkflowModal(contract, 'complete')}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                              title="Mark Completed"
                            >
                              <CheckCircleIcon className="w-3.5 h-3.5" />
                              Mark Completed
                            </button>
                          )}
                          {contract.is_recurring && (
                            <button
                              onClick={() => viewSchedule(contract)}
                              className="p-1 text-purple-600 hover:text-purple-900 hover:bg-purple-50 rounded-md transition-colors"
                              title="View Schedule"
                            >
                              <CalendarDaysIcon className="w-4 h-4" />
                            </button>
                          )}
                          {contract.is_recurring && contract.status === 'active' && (
                            <button
                              onClick={() => generateJobsFromContract(contract.id)}
                              className="p-1 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-md transition-colors"
                              title="Generate Jobs"
                            >
                              <PlayIcon className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteContract(contract)}
                            className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md transition-colors"
                            title="Delete Contract"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Contract Schedule Modal */}
      {scheduleModal.contract && (
        <ContractScheduleModal
          isOpen={scheduleModal.isOpen}
          onClose={closeScheduleModal}
          contract={scheduleModal.contract}
        />
      )}

      {/* Workflow Confirmation Modal */}
      {workflowModal.isOpen && workflowModal.contract && workflowModal.action && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
          onClick={closeWorkflowModal}
        >
          <div
            className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-5 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${workflowModal.action === 'send' ? 'bg-blue-100' : workflowModal.action === 'start' ? 'bg-yellow-100' : workflowModal.action === 'complete' ? 'bg-blue-100' : 'bg-green-100'}`}>
                  {workflowModal.action === 'send' ? (
                    <SendIcon className="w-5 h-5 text-blue-600" />
                  ) : workflowModal.action === 'start' ? (
                    <PlayIcon className="w-5 h-5 text-yellow-600" />
                  ) : workflowModal.action === 'complete' ? (
                    <CheckCircleIcon className="w-5 h-5 text-blue-600" />
                  ) : (
                    <PenToolIcon className="w-5 h-5 text-green-600" />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {workflowModal.action === 'send'
                      ? 'Send for Signature'
                      : workflowModal.action === 'start'
                      ? 'Start Work'
                      : workflowModal.action === 'complete'
                      ? 'Mark Completed'
                      : 'Mark as Signed'}
                  </h3>
                  <p className="text-sm text-gray-500">{workflowModal.contract.title}</p>
                </div>
              </div>
              <button
                onClick={closeWorkflowModal}
                disabled={workflowSaving}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <XIcon className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="p-5 space-y-3">
              {workflowModal.action === 'send' ? (
                <>
                  <p className="text-sm text-gray-700">
                    This will move the contract to <strong>Pending Signature</strong> status, indicating it has been sent to the customer for review.
                  </p>
                  <p className="text-xs text-gray-500">
                    Note: this updates the status only. To actually deliver the contract to the customer, send it via email or your preferred method separately.
                  </p>
                </>
              ) : workflowModal.action === 'start' ? (
                <p className="text-sm text-gray-700">
                  This will move the contract to <strong>In Process</strong>, indicating work
                  has begun. This also happens automatically when the first job starts.
                </p>
              ) : workflowModal.action === 'complete' ? (
                <p className="text-sm text-gray-700">
                  This will mark the contract as <strong>Completed</strong>. This also happens
                  automatically when the last open job is completed.
                </p>
              ) : workflowModal.contract.contract_type === 'as_needed' ? (
                <>
                  <p className="text-sm text-gray-700">
                    This will mark the contract as <strong>Active</strong> and set today as the
                    signed date. Jobs are created when needed — no auto schedule.
                  </p>
                  <p className="text-xs text-gray-500">
                    Confirm you have received the signed contract before proceeding.
                  </p>
                </>
              ) : workflowModal.contract.is_recurring ? (
                <>
                  <p className="text-sm text-gray-700">
                    This will mark the contract as <strong>Active</strong> and set today as the
                    signed date. Jobs will be generated for the next 90 days (clipped by the
                    contract end date).
                  </p>
                  <p className="text-xs text-gray-500">
                    Confirm you have received the signed contract before proceeding.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-700">
                    This will mark the contract as <strong>Signed</strong> (waiting to start) and
                    set today as the signed date. It moves to <strong>In Process</strong> when
                    work begins and <strong>Completed</strong> when the last job is done.
                  </p>
                  <p className="text-xs text-gray-500">
                    Confirm you have received the signed contract before proceeding.
                  </p>
                </>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={closeWorkflowModal}
                disabled={workflowSaving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmWorkflowAction}
                disabled={workflowSaving}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 ${
                  workflowModal.action === 'send'
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {workflowSaving
                  ? 'Working...'
                  : workflowModal.action === 'send'
                  ? 'Send for Signature'
                  : workflowModal.action === 'start'
                  ? 'Start Work'
                  : workflowModal.action === 'complete'
                  ? 'Mark Completed'
                  : workflowModal.contract.is_recurring ||
                      workflowModal.contract.contract_type === 'as_needed'
                  ? 'Confirm & Activate'
                  : 'Confirm & Mark Signed'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Visit Group Configurator Modal — surfaced when Mark Signed is blocked */}
      {vgConfigurator.isOpen && vgConfigurator.contract && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4"
          onClick={closeVisitGroupConfigurator}
        >
          <div
            className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-5xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-5 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Configure Visit Groups
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {vgConfigurator.contract.title} — before this recurring contract can be
                  activated, visit groups must be configured.
                </p>
              </div>
              <button
                onClick={closeVisitGroupConfigurator}
                disabled={vgConfigurator.saving}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <XIcon className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {vgConfigurator.loading ? (
                <div className="text-center text-gray-500 py-12">Loading...</div>
              ) : vgConfigurator.contractServices.length === 0 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-900">
                    This contract has no services configured yet. Add services before configuring
                    visit groups.
                  </p>
                </div>
              ) : (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-900">
                      We've pre-filled groups from the contract's services. Review the recurrence
                      days and service assignments, then click <strong>Save & Activate</strong>.
                    </p>
                  </div>
                  <VisitGroupEditor
                    visitGroups={vgConfigurator.visitGroups}
                    onChange={(groups) =>
                      setVgConfigurator((prev) => ({ ...prev, visitGroups: groups }))
                    }
                    contractServices={vgConfigurator.contractServices}
                    contractStartDate={vgConfigurator.contract.start_date}
                    contractEndDate={vgConfigurator.contract.end_date}
                    contractId={vgConfigurator.contract.id}
                    templates={visitGroupTemplates}
                    onSaveAsTemplate={saveGroupAsTemplate}
                  />
                </>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={closeVisitGroupConfigurator}
                disabled={vgConfigurator.saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={saveConfiguratorAndActivate}
                disabled={vgConfigurator.saving || vgConfigurator.loading}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {vgConfigurator.saving ? 'Saving...' : 'Save & Activate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </MobilePage>
  )
}
